import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, FormHelperText, Grid, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { renderClose, useDialog } from "../../common/dialog";
import { createEntityStore } from "../../common/entityStore";
import { getMostImportantValidationMessage, propBoolField, propEnumField, propNumberField, propSimpleEnumField, propTextField } from "../../common/entityStoreForm";
import { propCurrency, propLengthMax, propMinValue, propRequired, propValidValue, propValueValidator } from "../../common/entityStoreValidators";
import { InventoryArticleDto, SaleArticleDetailDto, SaleArticleDetailProductDto, SaleArticleDto } from "./types";
import { EntityStoreSaveButton } from "../../common/SaveButton";
import { UseListStore, createListStore } from "../../common/listStore";
import { combineLatest, distinctUntilChanged, map } from "rxjs";
import { EnumItemDto, MailProductCode, packagesTypes } from "../../common/enum";
import { listHeaderItems, renderListStore } from "../../common/listStoreUi";
import { Edit, Delete, Add } from "@mui/icons-material";
import { SaleArticleDetailPackageItemDialog } from "./SaleArticleDetailPackageItemDialog";
import shallow from "zustand/shallow";
import { newId, useRxJsSub } from "../../common/utils";
import { keyBy, round, sum } from "lodash";

export const SaleArticleDetailDialog = (props: {
    dialogProps: DialogProps;
    close: () => void;
    saleArticle: SaleArticleDto;
    saleArticleDetail: SaleArticleDetailDto | null;
    onChange(): void;
    useProductsStore: UseListStore<SaleArticleDetailProductDto>;
    useSaleArticleDetailsStore: UseListStore<SaleArticleDetailDto>;
    useInventoryArticlesStore: UseListStore<InventoryArticleDto>;
    inventoryArticle: InventoryArticleDto;
}) => {
    const [useStore] = useState(() => {
        return createEntityStore<SaleArticleDetailEditDto>({
            data: props.saleArticleDetail ?? {
                saleArticleId: props.saleArticle.id,
                price: 0,
                useDiscount: false,
                categories: "",
                articleName: "",
                isOnlyForAuto: false,
                packageType: null,
                packageItems: props.inventoryArticle.packageItems.map(p => {
                    var ia = props.useInventoryArticlesStore.getState().items.find(r => r.id === p.inventoryArticleId);
                    return {
                        id: newId(),
                        inventoryArticleId: p.inventoryArticleId,
                        saleArticleId: ia?.saleArticles.length === 1 ? ia.saleArticles[0].id as number : null,
                        price: null,
                        quantity: p.quantity,
                        invalid: true,
                    };
                }) ?? [],
            },
            path: "articles/saleArticleDetails",
            id: props.saleArticleDetail != null ? props.saleArticleDetail.id : null,
            getNewId: p => p.id!,
            onSaved() {
                props.onChange();
                props.close();
            },
            onFetched() {
                useStore.getState().setServerDataProp("packageItemsError", undefined);
            },
            propValidators: [
                propValidValue("price", "priceFormatted"),
                propRequired("price"),
                propCurrency("price"),
                propMinValue("price", 0),

                propRequired("projectId"),
                propValueValidator("projectId", (p, e) => props.useSaleArticleDetailsStore.getState().items
                    .some(i => i.projectId === p && i.id !== e.id) && "Sales article detail with same project already exists."),

                propLengthMax("categories", 200),
                propLengthMax("articleName", 100),
                propValueValidator("packageItems", (_p, e) => e.packageItemsError),
            ]
        })
    });

    const [useSaleArticlesStore] = useState(() => createListStore<EnumItemDto & { baseQuantity: number; }>({
        data: props.useInventoryArticlesStore.getState().items$.pipe(map(p => ({
            items: p.items.flatMap(i => i.saleArticles.map(s => ({ id: s.id, code: s.code, name: s.code + " " + s.name, baseQuantity: s.baseQuantity }))),
            isFetching: p.isFetching,
        })))
    }));
    const [useSaleArticlesEnumStore] = useState(() => createListStore<EnumItemDto>({ data: useSaleArticlesStore.getState().items$ }));

    const product = props.useProductsStore(p => p.items.find(p => p.code === MailProductCode));
    const [useProjectsStore] = useState(() => createListStore<EnumItemDto>({
        data: props.useProductsStore.getState().items$.pipe(map(products => ({
            items: (products.items.find(p => p.code === MailProductCode)?.projects || [])
                .map(p => ({ id: p.id, name: p.codeAndName })),
            isFetching: products.isFetching
        }))),
    }));

    const { isNew, packageItemsValidationMessage, areItemsMatchingTemplate } = useStore(p => ({
        isNew: props.saleArticleDetail == null && p.clientData.id == null,
        packageItemsValidationMessage: getMostImportantValidationMessage(p, "packageItems"),
        areItemsMatchingTemplate: getAreItemsMatchingTemplate(props.inventoryArticle, p.clientData.packageItems),
    }));

    const [usePackageItemsStore] = useState(() => createListStore<SaleArticleDetailPackageItemEditDto>({
        data: useStore.getState().clientData$.pipe(map(p => p.data.packageItems), distinctUntilChanged(shallow), map(items => ({
            isFetching: false,
            items,
        }))),
        listProps: [
            { column: "quantity", label: "Quantity", width: 75, type: "number" },
            { column: "price", label: "Price", width: 75, type: "currency" },
            { column: "saleArticleId", label: "Article", flex: 1, enumStore: useSaleArticlesEnumStore },
        ],
        itemsPerPageMin: 1,
    }));

    useRxJsSub(() => useStore.getState().clientData$.pipe(map(p => ({ price: p.data.price, items: p.data.packageItems })), distinctUntilChanged(shallow))
        .subscribe(({ items, price }) => {
            useStore.getState().setClientDataProp("packageItemsError",
                items.some(p => p.quantity == null || p.price == null) ? "All package items must have price." :
                    items.some(p => p.quantity == null || p.quantity < 1) ? "All package items must have quantity." :
                        price != null && items.length > 0 && round(sum(items.map(p => p.quantity! * p.price!)), 3) !== price ? `Package items price sum does not match Project price.` :
                            undefined);
        }));

    const { selectedPackageItem, hasPackageItems } = usePackageItemsStore(p => ({
        selectedPackageItem: p.selectedItem,
        hasPackageItems: true,
    }));

    const [packageItemsWithNonStandardBaseQuantity, setPackageItemsWithNonStandardBaseQuantity] = useState<{ saleArticleId: number; name: string; baseQuantity: number; }[]>(() => []);
    useRxJsSub(() => combineLatest([useSaleArticlesStore.getState().items$, usePackageItemsStore.getState().items$]).subscribe(([saleArticles, packageItems]) => {
        const itemsWithNonStandardBaseQuantity = packageItems.items
            .map(p => {
                const sa = saleArticles.items.find(s => s.id === p.saleArticleId)!;
                return {
                    saleArticleId: sa.id as number,
                    name: sa.name!,
                    baseQuantity: sa.baseQuantity,
                };
            })
            .filter(p => p.baseQuantity !== 1);
        setPackageItemsWithNonStandardBaseQuantity(itemsWithNonStandardBaseQuantity);
    }));

    const addPackageItemDialog = useDialog((close, dialogProps) => <SaleArticleDetailPackageItemDialog close={close} dialogProps={dialogProps}
        useStore={useStore} packageItem={null} useInventoryArticlesStore={props.useInventoryArticlesStore} />, undefined, {
        usingCustomDialogTag: true,
    });
    const editPackageItemDialog = useDialog((close, dialogProps) => <SaleArticleDetailPackageItemDialog close={close} dialogProps={dialogProps}
        useStore={useStore} packageItem={selectedPackageItem!} useInventoryArticlesStore={props.useInventoryArticlesStore} />, undefined, {
        usingCustomDialogTag: true,
    });

    function isValidBasedOnTemplate(item: SaleArticleDetailPackageItemEditDto): boolean {
        const templateItem = props.inventoryArticle.packageItems.find(p => p.inventoryArticleId === item.inventoryArticleId);
        return templateItem ? templateItem.quantity === item.quantity : false;
    }

    return <Dialog {...props.dialogProps} maxWidth={hasPackageItems ? "md" : "xs"}><Box m={2}>
        {addPackageItemDialog.render()}
        {editPackageItemDialog.render()}
        <DialogTitle>{props.saleArticleDetail?.id == null ? "New sales article detail " : `Edit sales article detail`}</DialogTitle>
        {renderClose(props.close)}
        <DialogContent>
            <Grid container columnSpacing={2}>
                <Grid item container xs={hasPackageItems ? 5 : 12}>
                    <Grid item xs={12}><TextField value={props.saleArticle.code} label="Sales article code" disabled /></Grid>
                    <Grid item xs={12}><TextField value={props.saleArticle.name} label="Sales article description" disabled /></Grid>
                    <Grid item xs={12}><TextField value={product?.codeAndName || ""} label="Product" disabled /></Grid>
                    <Grid item xs={12}>{propEnumField(useStore)("projectId", "Project", useProjectsStore, { disabled: !isNew })}</Grid>
                    <Grid item xs={12}>{propNumberField(useStore)("price", "priceFormatted", "Project price", { isCurrency: true })}</Grid>
                    <Grid item xs={12}>{propBoolField(useStore)("useDiscount", "Use qty discount?")}</Grid>
                    <Grid item xs={12}>{propSimpleEnumField(useStore)("packageType", "Package type", packagesTypes, { addNone: true })}</Grid>
                    <Grid item xs={12}>{propTextField(useStore)("categories", "Categories")}</Grid>
                    <Grid item xs={12}>{propTextField(useStore)("articleName", "Article name")}</Grid>
                    <Grid item xs={12}>{propBoolField(useStore)("isOnlyForAuto", "Is only for auto?")}</Grid>
                </Grid>
                {hasPackageItems && <Grid item xs={7}>
                    <Typography variant="h6" mt={2}>Package items</Typography>
                    {renderListStore(usePackageItemsStore, {
                        onDoubleClick: item => {
                            editPackageItemDialog.show();
                        },
                        header: listHeaderItems([
                            ["edit", () => <Button color="edit" disabled={selectedPackageItem == null} title="Edit" onClick={() => {
                                editPackageItemDialog.show();
                            }}><Edit /></Button>],
                            ["add", () => <Button color="add" title="Add" onClick={() => {
                                addPackageItemDialog.show();
                            }}><Add /></Button>],
                            ["remove", () => <Button color="delete" disabled={selectedPackageItem == null} title="Delete" onClick={() => {
                                useStore.getState().setClientDataProp("packageItems", useStore.getState().clientData.packageItems.filter(p => p.saleArticleId !== selectedPackageItem!.saleArticleId));
                            }}><Delete /></Button>],
                        ]),
                        getRowClass: p => p.invalid ? ["redRibbon"] : !isValidBasedOnTemplate(p) ? ["warningRibbon"] : [],
                    })}
                    {packageItemsValidationMessage.firstMessageType === "error" && <FormHelperText error>{packageItemsValidationMessage.firstMessage}</FormHelperText>}
                    {!areItemsMatchingTemplate && <Alert severity="warning">Package items do not match the template:
                        {props.inventoryArticle.packageItems.map(item => (
                            <div key={item.id}>{item.quantity}x {item.articleCode} {item.articleName}</div>
                        ))}</Alert>}
                    {props.saleArticle.baseQuantity !== 1 && <Alert severity="info" sx={{ mt: 2 }}>This sales article has base quantity {props.saleArticle.baseQuantity}.</Alert>}
                    {packageItemsWithNonStandardBaseQuantity.length > 0 && <Alert severity="info" sx={{ mt: 2 }}>Package items with non-standard base quantity:
                        <ul>
                            {packageItemsWithNonStandardBaseQuantity.map(item => (
                                <li key={item.saleArticleId}>{item.name} (base quantity {item.baseQuantity})</li>
                            ))}
                        </ul>
                    </Alert>}
                </Grid>}
            </Grid>
        </DialogContent>
        <DialogActions>
            <EntityStoreSaveButton useStore={useStore} />
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </Box></Dialog >;
};

export interface SaleArticleDetailEditDto {
    id?: number;
    saleArticleId: number;
    projectId?: number;
    price: number;
    priceFormatted?: string;
    useDiscount: boolean;
    categories: string;
    articleName: string;
    isOnlyForAuto: boolean;
    packageType: number | null;
    packageItems: SaleArticleDetailPackageItemEditDto[];
    packageItemsError?: string;
}

export interface SaleArticleDetailPackageItemEditDto {
    id: number;
    inventoryArticleId?: number | null;
    saleArticleId: number | null;
    quantity: number | null;
    quantityFormatted?: string;
    price: number | null;
    priceFormatted?: string;
    invalid?: true;
    articleName?: string;
}

function getAreItemsMatchingTemplate(ia: InventoryArticleDto, items: SaleArticleDetailPackageItemEditDto[]): boolean {
    const templateItems = keyBy(ia.packageItems, p => p.inventoryArticleId);
    return ia.packageItems.length === items.length && items.every(p => p.inventoryArticleId != null &&
        templateItems[p.inventoryArticleId]?.quantity === p.quantity);
}